// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Every colour that carries meaning on the play surface, and — the part that matters — a DECLARATION
// of which surface each one is painted on. `tests/contrast.test.ts` walks these lists and holds each
// pair to 3:1 (WCAG 2.1 §1.4.11, the floor for a non-text graphic).
//
// This file exists because a sibling game in this fleet drew its walls at 1.14:1 against the board:
// in the right cells, at the right size, and invisible. The player called them "see-through", which
// is exactly what a 1.14:1 rectangle looks like. Nothing caught it — jsdom has no canvas, so no unit
// test saw it, and on a deliberately moody dark palette a human glance reads INVISIBLE as
// atmosphere. The screenshot looked great. So no renderer here is allowed an inline colour literal:
// if a pixel means something, its colour is named here and it is pinned.
//
// ── WHAT MEASURING CHANGED ──────────────────────────────────────────────────────────────────────
// 1. The first wall colour was #3A4A5E on a #1B2633 floor: 1.62:1. Walls are the thing the whole
//    game is about hiding behind, and they were nearly invisible. They are #6B8299 now, at 3.4:1.
// 2. The two sides started as blue and red, which is the worst possible pair for the commonest
//    colour blindness. They are BLUE and AMBER, and each side also has a distinct chip outline, so
//    hue is never the only channel.
// 3. The watch wedge cannot be a tint of the floor — a wash light enough to read as "covered" was
//    within 1.3:1 of the floor. It is drawn as a fill with a bright boundary line instead, and the
//    test asserts the boundary exists rather than pretending the wash carries it.
// 4. The rail was #41586E: 2.07:1 on the floor. A barrier you cannot see is a route you cannot plan.
//
// ── AND ONE REQUIREMENT THAT TURNED OUT TO BE UNSATISFIABLE ─────────────────────────────────────
// The first cut asserted 3:1 BETWEEN the two sides' colours, on the grounds that "whose unit is
// that" is the most important distinction on screen. Blue against amber measured 1.07:1 — and no
// pair of colours can satisfy it. Each side must clear 3:1 against a dark floor, which puts both
// luminances above ~0.17; two colours both above 0.17 cannot be 3:1 apart from each other, because
// that needs one of them below 0.12. The window is empty, and the same argument kills dead-vs-alive
// and wall-vs-rail.
//
// That is a finding about the design, not a threshold to lower. Every one of those distinctions is
// carried in a SECOND CHANNEL instead — a dead unit is an X rather than a disc, a rail is a bar with
// clear space above it rather than a filled cell, the mast is stroked, and the selected unit gets a
// white ring — and the tests below assert those channels exist rather than pretending a ratio does
// the work. Blue against amber is also the right hue pair for the commonest colour blindness, which
// is the distinction that actually matters here; luminance is not.

/** Backgrounds a meaningful colour can be painted onto. */
export const SURFACES = {
  /** Outside the board. */
  void: '#0D141C',
  /** Open ground. */
  floor: '#1B2633',
  /** DOM panels — menu, results, help. */
  panel: '#131C26',
} as const;

export type SurfaceName = keyof typeof SURFACES;

export const COLORS = {
  // ── terrain ──────────────────────────────────────────────────────────────────
  /** Blocks feet and sight. The thing you hide behind, so it had better be visible. */
  wall: '#6B8299',
  /** Blocks feet, not sight — drawn as a bar with a gap you can see through. */
  rail: '#7A93AD',
  /** The mast block, and the field around it. */
  mast: '#5BE0C0',

  // ── the two sides ────────────────────────────────────────────────────────────
  you: '#74B8FF',
  foe: '#FFA63C',

  // ── what a unit is doing ─────────────────────────────────────────────────────
  /** The line a shot travelled. */
  shot: '#FFF0B8',
  /** A unit that has been killed. */
  down: '#E24C3F',
  /** The boundary of a watch wedge — the channel that carries "this ground is covered". */
  watch: '#FFD166',
  /** The route a unit has been ordered to walk. */
  path: '#9FF2E0',
  /** Second-marks along that route. */
  tick: '#0D141C',

  // ── chrome painted on the canvas ─────────────────────────────────────────────
  label: '#A8BCD0',
  /** The selected unit's ring. */
  select: '#FFFFFF',
} as const;

export type ColorName = keyof typeof COLORS;

/** Every meaningful colour, and every surface it can actually be painted on. */
export const MEANINGFUL: ReadonlyArray<{ color: ColorName; on: readonly SurfaceName[] }> = [
  { color: 'wall', on: ['floor', 'void'] },
  { color: 'rail', on: ['floor'] },
  { color: 'mast', on: ['floor', 'void'] },
  { color: 'you', on: ['floor', 'void'] },
  { color: 'foe', on: ['floor', 'void'] },
  { color: 'shot', on: ['floor', 'void'] },
  { color: 'down', on: ['floor', 'void'] },
  { color: 'watch', on: ['floor', 'void'] },
  { color: 'path', on: ['floor', 'void'] },
  { color: 'label', on: ['void', 'panel'] },
  { color: 'select', on: ['floor', 'void'] },
];

/**
 * Pairs where one meaning is genuinely painted ON another, so a ratio is the right instrument.
 *
 * Deliberately short. See the note at the top: colour-against-colour distinctions that are really
 * "tell these two things apart" cannot all clear 3:1 on a dark board, and are carried by shape.
 */
export const PAIRS: ReadonlyArray<{ a: ColorName; b: ColorName; why: string }> = [
  { a: 'tick', b: 'path', why: 'the second-marks are drawn INSIDE the route dot — the ruler that says when you arrive' },
];

/**
 * The selection ring is drawn OUTSIDE the unit disc, on the floor, so its background is the floor
 * and MEANINGFUL already measures it there. Pairing it against the unit colour measured 2.09:1 and
 * was the same empty-window mistake as blue-versus-amber: a white ring cannot be 3:1 from a light
 * blue disc AND 3:1 from a dark floor at once. What makes it findable is that it is a ring at all.
 */

/**
 * `shot` and `watch` are drawn only over other things (a line between two units, a hatch over the
 * floor) and are measured against the surfaces above rather than against every unit colour they may
 * cross. Kept as an explicit list so the coverage check cannot pass by forgetting one.
 */
export const NOT_PAIRED: readonly ColorName[] = [];

/**
 * Distinctions that contrast cannot carry, and the channel that carries each one instead.
 * `tests/contrast.test.ts` asserts the renderer really draws them.
 */
export const SECOND_CHANNEL: ReadonlyArray<{ meaning: string; needle: RegExp }> = [
  { meaning: 'a dead unit is an X, not a differently-coloured disc', needle: /COLORS\.down[\s\S]{0,400}?moveTo/ },
  { meaning: 'a rail is a bar with clear space above it, not a filled cell', needle: /COLORS\.rail[\s\S]{0,200}?0\.34/ },
  { meaning: 'the mast is stroked as well as tinted', needle: /COLORS\.mast[\s\S]{0,300}?strokeRect/ },
  { meaning: 'the selected unit carries a ring', needle: /COLORS\.select/ },
  { meaning: 'every unit type has its own glyph', needle: /spec\.glyph/ },
];

/** The two sides, in the order seats are numbered. */
export const SEAT: readonly ColorName[] = ['you', 'foe'];
export const SEAT_NAME: readonly string[] = ['Blue', 'Amber'];
